import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCalls } from '@/entities/call'
import { formatDateTime } from '@/shared/lib/time'
import styles from './CommandPalette.module.css'

interface Command {
  id: string
  group: string
  title: string
  note?: string
  meta?: string
  run: () => void
}

interface CommandPaletteProps {
  open: boolean
  onClose: () => void
}

/**
 * Переход по кабинету с клавиатуры.
 *
 * Разделов девять, и половина из них — то, куда заходят раз в неделю.
 * Искать их мышью в колонке дольше, чем набрать три буквы. Заодно сюда
 * попадают звонки: их становится много, и «найти тот разговор, где
 * согласились» через список — это листание.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [cursor, setCursor] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // Звонки подтягиваются, только когда окно открыто: иначе список
  // запрашивается на каждой странице кабинета впустую.
  const calls = useCalls({})

  const commands = useMemo<Command[]>(() => {
    const go = (path: string) => () => {
      navigate(path)
      onClose()
    }

    const sections: Command[] = [
      { id: 'nav-home', group: 'Разделы', title: 'Обзор', run: go('/') },
      { id: 'nav-test', group: 'Разделы', title: 'Тестовый звонок', note: 'позвонить агенту', run: go('/test-call') },
      { id: 'nav-calls', group: 'Разделы', title: 'Звонки', run: go('/calls') },
      { id: 'nav-agenda', group: 'Разделы', title: 'Что сделать', note: 'календарь перезвонов', run: go('/agenda') },
      { id: 'nav-profile', group: 'Настройки', title: 'Анкета агента', note: 'чем занимаетесь, что предлагаете', run: go('/settings/profile') },
      { id: 'nav-voice', group: 'Настройки', title: 'Голос', note: 'живость, темп, перебивание', run: go('/settings/voice') },
      { id: 'nav-hours', group: 'Настройки', title: 'Часы звонков', run: go('/settings/hours') },
      { id: 'nav-prompt', group: 'Настройки', title: 'Промпт', note: 'что уходит модели', run: go('/settings/prompt') },
      { id: 'nav-company', group: 'Настройки', title: 'Компания', note: 'название, часовой пояс', run: go('/settings/company') },
    ]

    const recent: Command[] = (open ? (calls.data ?? []) : []).slice(0, 20).map((call) => ({
      id: `call-${call.id}`,
      group: 'Звонки',
      title: call.report?.outcomeLabel ?? 'Разговор',
      note: call.report?.summary ?? formatDateTime(call.startedAt ?? call.createdAt),
      meta: call.report ? String(call.report.score) : undefined,
      run: go(`/calls/${call.id}`),
    }))

    return [...sections, ...recent]
  }, [calls.data, navigate, onClose, open])

  const found = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return commands

    return commands.filter((command) =>
      `${command.title} ${command.note ?? ''}`.toLowerCase().includes(needle),
    )
  }, [commands, query])

  useEffect(() => {
    if (!open) return
    setQuery('')
    setCursor(0)
    // Фокус после отрисовки: иначе поле ещё не существует.
    const id = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [open])

  useEffect(() => {
    setCursor(0)
  }, [query])

  if (!open) return null

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      onClose()
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor((c) => Math.min(c + 1, found.length - 1))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor((c) => Math.max(c - 1, 0))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      found[cursor]?.run()
    }
  }

  let lastGroup = ''

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.window}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        role="dialog"
        aria-label="Поиск по кабинету"
      >
        <div className={styles.search}>
          <span className={styles.icon}>
            <SearchIcon />
          </span>
          <input
            ref={inputRef}
            className={styles.input}
            placeholder="Поиск или переход…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <span className={styles.hint}>Esc</span>
        </div>

        <div className={styles.list}>
          {found.length === 0 ? (
            <p className={styles.empty}>Ничего не нашлось</p>
          ) : (
            found.map((command, i) => {
              const header = command.group !== lastGroup ? command.group : null
              lastGroup = command.group

              return (
                <div key={command.id}>
                  {header && <div className={styles.group}>{header}</div>}
                  <button
                    type="button"
                    className={`${styles.item} ${i === cursor ? styles.itemActive : ''}`}
                    onMouseEnter={() => setCursor(i)}
                    onClick={command.run}
                  >
                    <span className={styles.itemIcon}>
                      {command.group === 'Звонки' ? <CallIcon /> : <ArrowIcon />}
                    </span>
                    <span className={styles.itemBody}>
                      <span className={styles.itemTitle}>{command.title}</span>
                      {command.note && <span className={styles.itemNote}>{command.note}</span>}
                    </span>
                    {command.meta && <span className={styles.itemMeta}>{command.meta}</span>}
                  </button>
                </div>
              )
            })
          )}
        </div>

        <div className={styles.footer}>
          <span>
            <span className={styles.key}>↑↓</span>выбор
          </span>
          <span>
            <span className={styles.key}>↵</span>перейти
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * Ctrl+K и Cmd+K на всё приложение.
 *
 * Слушатель на документе, а не на поле: сочетание должно работать
 * откуда угодно, в том числе когда фокус в форме.
 */
export function useCommandPalette() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        // Браузер вешает на это сочетание свой поиск — забираем себе.
        event.preventDefault()
        setOpen((v) => !v)
      }
    }

    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  return { open, setOpen }
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10.8 10.8L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M3 7H11M11 7L7.5 3.5M11 7L7.5 10.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CallIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M5 1.9L6.1 4.4L4.9 5.6C5.5 6.9 7.1 8.5 8.4 9.1L9.6 7.9L12.1 9V11.2C12.1 11.7 11.7 12.1 11.2 12.1C5.9 11.8 2.2 8.1 1.9 2.8C1.9 2.3 2.3 1.9 2.8 1.9H5Z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  )
}
